import { AvailabilityState, IProduktDetailsVerfuegbarkeit, IProduktVerfuegbarkeit, Product, TextNodeType } from '@mwe/models';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit, output, signal } from '@angular/core';
import { capitalizeString, containsProductCategory, everyTrue, isEqualCategoryString, isProductCategory } from '@mwe/utils';
import { ProductCategoryEnum } from '@mwe/constants';
import { EnvironmentService, RelocationService } from '@mwe/services';
import { CardSelectableComponent } from '../../cards/card-selectable/card-selectable.component';
import { ProductIconComponent } from '../../product-icon/product-icon.component';
import { HeadlineService } from '../../headline/headline.service';
import { CorrelatedCompanyReferencePipe, TranslateTariffClassPipe } from '@mwe/ui/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'mwe-wizard-product-card-container',
  templateUrl: './wizard-product-card-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardSelectableComponent,
    ProductIconComponent,
    CorrelatedCompanyReferencePipe,
    TranslatePipe,
    TranslateTariffClassPipe,
    AsyncPipe,
  ],
})
export class WizardProductCardContainerComponent implements OnInit {
  private readonly relocationService = inject(RelocationService);
  private readonly environmentService = inject(EnvironmentService);
  readonly headlineService = inject(HeadlineService);

  readonly product = input.required<Product>();
  readonly productAvailabilities = input<IProduktVerfuegbarkeit[]>([]);
  readonly isAvailableAtNewAddress = input<boolean>(false);
  readonly wasInitiallyOrPreviouslyChecked = input<boolean>(false);
  readonly showCurrentProductTariff = input<boolean>(false);
  readonly showTariffClasses = input<boolean>(false);
  readonly showAccountNumber = input<boolean>(false);
  readonly isDisabled = input<boolean>(false);
  readonly isReadonly = input<boolean>(false);
  readonly isRelocationProcess = input<boolean>(false);
  readonly isOrdered = model<boolean>(false);
  readonly headingNodeType = input<TextNodeType>('h2');

  readonly toggleState = output<boolean>();

  readonly productCategory = computed(() => this.product().category);
  readonly showTVInfoText = computed(() => {
    return (
      !this.isRelocationProcess() &&
      this.isDisabled() &&
      (isProductCategory(this.productCategory(), ProductCategoryEnum.INTERNET) ||
        isProductCategory(this.productCategory(), ProductCategoryEnum.IPTV))
    );
  });

  readonly showInformationText = signal(false);
  readonly availabilityDetails = computed<IProduktDetailsVerfuegbarkeit[]>(() => {
    if (isProductCategory(this.productCategory(), ProductCategoryEnum.FERNWAERME)) {
      return (
        this.productAvailabilities()?.find(element => isProductCategory(element.sparte, ProductCategoryEnum.FERNWAERME))?.details ?? []
      );
    }

    return [];
  });
  readonly informationTextProductCategory = computed(() => capitalizeString(this.productCategory()));
  readonly containerTestSelector = computed(() => `wizard-product-card-container-${this.productCategory()?.toLowerCase()}`);

  readonly tariffClasses = computed<string[]>(() => {
    const customerType = this.environmentService.getPortalCustomerType();

    if (everyTrue(!this.showCurrentProductTariff(), this.isAvailableAtNewAddress())) {
      if (
        isProductCategory(this.productCategory(), ProductCategoryEnum.STROM) ||
        isProductCategory(this.productCategory(), ProductCategoryEnum.GAS)
      ) {
        return [this.relocationService.getConfigTariffs()?.[this.productCategory().toLowerCase()]?.[customerType]?.name];
      }

      if (isProductCategory(this.productCategory(), ProductCategoryEnum.FERNWAERME) && this.availabilityDetails()) {
        return this.availabilityDetails().map(element => capitalizeString(element?.lieferkomponente));
      }

      return [];
    }
    return this.product().tariffClasses;
  });

  readonly fakeProductForTariffClassTranslations = computed<Product>(() => {
    return { category: this.productCategory(), tariffClasses: this.tariffClasses() } as Product;
  });

  readonly accountNumber = computed(() => {
    const product = this.product();
    const accountNumber = product.category === 'Fernwaerme' ? product.accountNumber : product.customerNumber;

    return { accountNumber };
  });

  ngOnInit() {
    this.initializeComponentData();
  }

  onProductSelect(isChecked: boolean) {
    this.isOrdered.set(isChecked);
    this.toggleState.emit(isChecked);
  }

  private initializeComponentData() {
    this.headlineService.setReferenceHeadline(this.headingNodeType());
    this.onProductSelect(this.wasInitiallyOrPreviouslyChecked());
    this.showInformation(this.product());
  }

  private showInformation(product: Product) {
    this.showInformationText.set(false);

    const sameCategoryAvailableProduct = this.productAvailabilities()?.find(
      p =>
        containsProductCategory(product.category, [ProductCategoryEnum.STROM, ProductCategoryEnum.GAS, ProductCategoryEnum.FERNWAERME]) &&
        isEqualCategoryString(p.sparte, product.category),
    );

    if (!sameCategoryAvailableProduct) return;

    this.showInformationText.set(
      sameCategoryAvailableProduct.verfuegbar === AvailabilityState.UNKNOWN ||
        sameCategoryAvailableProduct.verfuegbar === AvailabilityState.TECHNICAL_ERROR ||
        sameCategoryAvailableProduct.verfuegbar === AvailabilityState.BUILDING,
    );
  }
}
